import { cloneElement, useEffect, useId, useState } from "react";
import type { CSSProperties, FocusEvent, MouseEvent, ReactElement, ReactNode } from "react";
import { createPortal } from "react-dom";
import { theme } from "../theme";

// ── Tooltip ───────────────────────────────────────────────────────────────
// A dependency-free hover / focus tooltip. The label is portalled to <body>
// with fixed positioning so an overflow-hidden container (the collapsed
// sidebar rail) cannot clip it. The wrapped element keeps its own handlers.

type Side = "right" | "left" | "top" | "bottom";

interface HandlerProps {
  onMouseEnter?: (e: MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (e: MouseEvent<HTMLElement>) => void;
  onFocus?: (e: FocusEvent<HTMLElement>) => void;
  onBlur?: (e: FocusEvent<HTMLElement>) => void;
  "aria-describedby"?: string;
}

interface TooltipProps {
  label: ReactNode;
  side?: Side;
  /** Render the child untouched (e.g. when the sidebar is expanded and the label is visible). */
  disabled?: boolean;
  children: ReactElement<HandlerProps>;
}

const GAP = 8;

function position(rect: DOMRect, side: Side): CSSProperties {
  switch (side) {
    case "left":
      return { top: rect.top + rect.height / 2, left: rect.left - GAP, transform: "translate(-100%, -50%)" };
    case "top":
      return { top: rect.top - GAP, left: rect.left + rect.width / 2, transform: "translate(-50%, -100%)" };
    case "bottom":
      return { top: rect.bottom + GAP, left: rect.left + rect.width / 2, transform: "translate(-50%, 0)" };
    case "right":
    default:
      return { top: rect.top + rect.height / 2, left: rect.right + GAP, transform: "translate(0, -50%)" };
  }
}

export function Tooltip({ label, side = "right", disabled = false, children }: TooltipProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const id = useId();
  const open = !disabled && rect !== null && label !== null && label !== undefined && label !== "";

  // Any scroll or Escape hides it: the anchor may have moved or gone away.
  useEffect(() => {
    if (!open) return;
    const hide = () => setRect(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") hide(); };
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (disabled) return children;

  const childProps = children.props;
  const show = (el: HTMLElement) => setRect(el.getBoundingClientRect());
  const hide = () => setRect(null);

  const child = cloneElement(children, {
    onMouseEnter: (e: MouseEvent<HTMLElement>) => { childProps.onMouseEnter?.(e); show(e.currentTarget); },
    onMouseLeave: (e: MouseEvent<HTMLElement>) => { childProps.onMouseLeave?.(e); hide(); },
    onFocus: (e: FocusEvent<HTMLElement>) => { childProps.onFocus?.(e); show(e.currentTarget); },
    onBlur: (e: FocusEvent<HTMLElement>) => { childProps.onBlur?.(e); hide(); },
    "aria-describedby": open ? id : childProps["aria-describedby"],
  });

  return (
    <>
      {child}
      {open && rect && createPortal(
        <div
          id={id}
          role="tooltip"
          style={{
            position: "fixed",
            ...position(rect, side),
            zIndex: 3000,
            pointerEvents: "none",
            background: "var(--c-heading)",
            color: "var(--c-bg)",
            fontFamily: theme.font,
            fontSize: 11.5,
            fontWeight: 600,
            lineHeight: 1.3,
            padding: "5px 8px",
            borderRadius: 3,
            boxShadow: "var(--c-sheetShadow)",
            whiteSpace: "nowrap",
            maxWidth: 280,
          }}
        >
          {label}
        </div>,
        document.body,
      )}
    </>
  );
}
